import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, Trash2, Search, Phone, Mail, Building2, MapPin, Users,
  Calendar, Loader2, Copy, Inbox, LogOut, Lock, Download, Radio,
} from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SITE } from "@/lib/site";

// Admin panels lazy-load so the heavy blog corpus (AdminContent) and the
// charting library (AdminInsights/AdminRankings) stay out of the public main bundle.
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminHomepage = lazy(() => import("@/pages/admin/AdminHomepage"));
const AdminInsights = lazy(() => import("@/pages/admin/AdminInsights"));
const AdminRankings = lazy(() => import("@/pages/admin/AdminRankings"));

const AdminPanelFallback = (
  <div className="mt-8 flex items-center gap-2 text-sm text-[var(--tx-text-2)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading panel…</div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_COLORS = {
  new: "text-[var(--tx-gold)] border-[var(--tx-gold)]/40",
  contacted: "text-blue-300 border-blue-400/40",
  quoted: "text-purple-300 border-purple-400/40",
  won: "text-green-300 border-green-400/40",
  lost: "text-red-300 border-red-400/40",
};

export default function AdminLeads() {
  usePageMeta({ title: "Admin — Enquiry Leads | TripTrix Voyages", description: "Manage TripTrix Voyages enquiry leads." });
  const [token, setToken] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("leads");

  const logout = () => { setToken(""); };
  const cfg = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/enquiries?limit=500`, cfg()),
        axios.get(`${API}/enquiries/stats`, cfg()),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) { setToken(""); }
      else toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, [cfg]);

  useEffect(() => { if (token) load(); }, [load, token]);

  const patch = async (id, changes) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${id}`, changes, cfg());
      setLeads((prev) => prev.map((x) => (x.id === id ? data : x)));
      if (changes.status) { toast.success(`Marked as ${changes.status}`); load(); }
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/enquiries/${id}`, cfg());
      setLeads((prev) => prev.filter((x) => x.id !== id));
      toast.success("Lead deleted");
      load();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const copyLead = (l) => {
    const text = `Ref: ${l.ref}\nAgency: ${l.agency || "-"}\nName: ${l.name}\nEmail: ${l.email}\nPhone: ${l.phone}\nDestination: ${l.destination}\nMonth: ${l.travel_month}\nTravellers: ${l.travellers}\nSource: ${l.source || "Website"}\nMessage: ${l.message || "-"}`;
    navigator.clipboard.writeText(text);
    toast.success("Lead details copied");
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/enquiries/export`, { ...cfg(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `triptrix-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  const filtered = leads.filter((l) => {
    const matchesFilter = filter === "all" || l.status === filter;
    const hay = `${l.name} ${l.agency} ${l.email} ${l.phone} ${l.destination} ${l.ref}`.toLowerCase();
    return matchesFilter && hay.includes(q.toLowerCase());
  });

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  if (!token) return <AdminLogin onSuccess={(t) => setToken(t)} />;

  return (
    <section className="min-h-screen pt-32 pb-24" data-testid="admin-leads">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.24em] text-[var(--tx-gold)]">TripTrix Admin</span>
            <h1 className="mt-3 font-display font-light tracking-tight text-4xl md:text-6xl">Enquiry Leads</h1>
            <p className="mt-3 text-[var(--tx-text-2)]">Every enquiry submitted from your website lands here.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-[var(--tx-border)] px-5 py-2.5 text-sm hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-[var(--tx-border)] px-5 py-2.5 text-sm hover:border-red-400/60 hover:text-red-300 transition-colors duration-300">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-3 border-b border-[var(--tx-border)]">
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`pb-3 text-sm transition-colors duration-300 border-b-2 -mb-px ${tab === "leads" ? "border-[var(--tx-gold)] text-[var(--tx-gold)]" : "border-transparent text-[var(--tx-text-2)] hover:text-[var(--tx-text)]"}`}>Enquiry Leads</button>
          <button onClick={() => setTab("content")} data-testid="tab-content" className={`pb-3 text-sm transition-colors duration-300 border-b-2 -mb-px ${tab === "content" ? "border-[var(--tx-gold)] text-[var(--tx-gold)]" : "border-transparent text-[var(--tx-text-2)] hover:text-[var(--tx-text)]"}`}>Website Content</button>
          <button onClick={() => setTab("insights")} data-testid="tab-insights" className={`pb-3 text-sm transition-colors duration-300 border-b-2 -mb-px ${tab === "insights" ? "border-[var(--tx-gold)] text-[var(--tx-gold)]" : "border-transparent text-[var(--tx-text-2)] hover:text-[var(--tx-text)]"}`}>Insights</button>
          <button onClick={() => setTab("rankings")} data-testid="tab-rankings" className={`pb-3 text-sm transition-colors duration-300 border-b-2 -mb-px ${tab === "rankings" ? "border-[var(--tx-gold)] text-[var(--tx-gold)]" : "border-transparent text-[var(--tx-text-2)] hover:text-[var(--tx-text)]"}`}>Rankings</button>
          <button onClick={() => setTab("homepage")} data-testid="tab-homepage" className={`pb-3 text-sm transition-colors duration-300 border-b-2 -mb-px ${tab === "homepage" ? "border-[var(--tx-gold)] text-[var(--tx-gold)]" : "border-transparent text-[var(--tx-text-2)] hover:text-[var(--tx-text)]"}`}>Homepage</button>
        </div>

        <Suspense fallback={AdminPanelFallback}>
          {tab === "content" && <div className="mt-8"><AdminContent cfg={cfg} /></div>}
          {tab === "insights" && <div className="mt-8"><AdminInsights cfg={cfg} /></div>}
          {tab === "rankings" && <div className="mt-8"><AdminRankings cfg={cfg} /></div>}
          {tab === "homepage" && <div className="mt-8"><AdminHomepage cfg={cfg} /></div>}
        </Suspense>

        {tab === "leads" && (<>
        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-5">
            <div className="text-3xl font-display font-light text-[var(--tx-gold)]">{stats.total}</div>
            <div className="mt-1 text-xs uppercase tracking-[0.16em] text-[var(--tx-text-2)]">Total</div>
          </div>
          {STATUSES.map((s) => (
            <div key={s} className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-5">
              <div className="text-3xl font-display font-light">{stats.by_status?.[s] ?? 0}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.16em] text-[var(--tx-text-2)] capitalize">{s}</div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--tx-text-2)]" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, agency, email, phone, ref…" className="pl-10 bg-[var(--tx-bg-2)] border-[var(--tx-border)] rounded-none h-12" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-filter" className="w-full sm:w-48 bg-[var(--tx-bg-2)] border-[var(--tx-border)] rounded-none h-12"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
            </SelectContent>
          </Select>
          <button
            onClick={exportCsv}
            data-testid="admin-export-csv"
            disabled={leads.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-none border border-[var(--tx-gold)]/50 bg-[var(--tx-gold)]/10 px-5 h-12 text-sm text-[var(--tx-gold)] hover:bg-[var(--tx-gold)] hover:text-[var(--tx-bg)] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        {/* List */}
        <div className="mt-8 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[var(--tx-text-2)]"><Loader2 className="h-6 w-6 animate-spin mr-3" /> Loading leads…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-[var(--tx-text-2)]">
              <Inbox className="h-10 w-10 mb-4" />
              {leads.length === 0 ? "No enquiries yet — they'll appear here as they come in." : "No leads match your search."}
            </div>
          )}
          {filtered.map((l) => (
            <div key={l.id} data-testid={`lead-${l.ref}`} className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-6">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs text-[var(--tx-gold)]">{l.ref}</span>
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs capitalize ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span>
                    {l.source && <span data-testid={`lead-source-${l.ref}`} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--tx-border)] px-2.5 py-0.5 text-xs text-[var(--tx-text-2)]"><Radio className="h-3 w-3 text-[var(--tx-gold)]" />{l.source}</span>}
                    <span className="inline-flex items-center gap-1.5 text-xs text-[var(--tx-text-2)]"><Calendar className="h-3.5 w-3.5" />{fmtDate(l.created_at)}</span>
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-light">{l.name}</h3>
                  <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[var(--tx-text-2)]">
                    {l.agency && <span className="inline-flex items-center gap-2"><Building2 className="h-4 w-4 text-[var(--tx-gold)]" />{l.agency}</span>}
                    <a href={`tel:${l.phone}`} className="inline-flex items-center gap-2 hover:text-[var(--tx-gold)]"><Phone className="h-4 w-4 text-[var(--tx-gold)]" />{l.phone}</a>
                    {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 hover:text-[var(--tx-gold)]"><Mail className="h-4 w-4 text-[var(--tx-gold)]" />{l.email}</a>}
                    <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[var(--tx-gold)]" />{l.destination} · {l.travel_month}</span>
                    <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-[var(--tx-gold)]" />{l.travellers} traveller(s)</span>
                  </div>
                  {l.message && <p className="mt-3 text-sm text-[var(--tx-text)]/80 border-l-2 border-[var(--tx-border)] pl-3">{l.message}</p>}

                  <div className="mt-4">
                    <label className="text-xs uppercase tracking-[0.16em] text-[var(--tx-text-2)]">Private notes</label>
                    <Textarea
                      data-testid={`lead-notes-${l.ref}`}
                      defaultValue={l.notes}
                      onBlur={(e) => { if (e.target.value !== (l.notes || "")) patch(l.id, { notes: e.target.value }); }}
                      placeholder="Add follow-up notes (saved when you click away)…"
                      className="mt-2 bg-[var(--tx-bg)] border-[var(--tx-border)] rounded-none h-20 text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-row lg:flex-col gap-3 lg:w-52">
                  <Select value={l.status} onValueChange={(v) => patch(l.id, { status: v })}>
                    <SelectTrigger data-testid={`lead-status-${l.ref}`} className="bg-[var(--tx-bg)] border-[var(--tx-border)] rounded-none h-11 capitalize"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}</SelectContent>
                  </Select>
                  <a href={`https://wa.me/${l.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-sm text-white hover:opacity-90 transition-opacity">WhatsApp</a>
                  <button onClick={() => copyLead(l)} className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--tx-border)] px-4 py-2.5 text-sm hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors"><Copy className="h-4 w-4" /> Copy</button>
                  <button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.ref}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-red-500/40 text-red-300 px-4 py-2.5 text-sm hover:bg-red-500/10 transition-colors"><Trash2 className="h-4 w-4" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>)}

        <p className="mt-10 text-xs text-[var(--tx-text-2)]">Signed in as owner · {SITE.name}. Tip: bookmark this page (/admin) to manage leads anytime.</p>
      </div>
    </section>
  );
}


function AdminLogin({ onSuccess }) {
  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Enter your admin email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-otp`, { email: email.trim() });
      toast.success("If that email is registered, a 6-digit code is on its way.");
      setStep("code");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send the code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (code.trim().length < 4) { toast.error("Enter the code from your email"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { email: email.trim(), code: code.trim() });
      toast.success("Welcome back");
      onSuccess(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center px-6 pt-32 pb-24" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-[var(--tx-gold)]/40 text-[var(--tx-gold)]">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-5 font-display text-4xl font-light">Admin Login</h1>
          <p className="mt-2 text-sm text-[var(--tx-text-2)]">
            {step === "email"
              ? "Enter your email — we'll send a one-time code."
              : `We sent a 6-digit code to ${email}.`}
          </p>
        </div>

        {step === "email" ? (
          <form onSubmit={requestOtp} className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[var(--tx-text-2)]">Email</label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-2 bg-[var(--tx-bg)] border-[var(--tx-border)] rounded-none h-12" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-request-otp" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--tx-gold)] px-8 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {loading ? "Sending code…" : "Send login code"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[var(--tx-text-2)]">6-digit code</label>
              <Input
                data-testid="login-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                className="mt-2 bg-[var(--tx-bg)] border-[var(--tx-border)] rounded-none h-12 tracking-[0.5em] text-center text-lg"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="login-verify-otp" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--tx-gold)] px-8 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {loading ? "Verifying…" : "Sign in"}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep("email"); setCode(""); }} className="text-[var(--tx-text-2)] hover:text-[var(--tx-gold)] transition-colors">← Change email</button>
              <button type="button" onClick={requestOtp} disabled={loading} data-testid="login-resend-otp" className="text-[var(--tx-gold)] hover:text-[var(--tx-text)] transition-colors disabled:opacity-60">Resend code</button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
